import { DeleteOutline, FileUploadOutlined } from '@mui/icons-material';
import { Box, Button, CircularProgress, IconButton, Link, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import ConfirmationModal from '../../components/ConfirmationModal';
import { useCreateAttachments, useDestroyAttachments, useGetAttachmentsByOwner } from '../../services/react-query/attachments';
import { Attachment, AttachmentOwnerType } from '../../types/Attachment';
import { Loading } from '../../uikit';
import FileInput from '../../uikit/fileInput/FileInput';
import { JsonifiedFile } from '../../utility/functions';
import { formatSize, getFileTypeIcon } from '../../utility/Attachment';
import { humanizeTimestamp } from '../../utility/dateUtils';
import _ from 'lodash';
import { attachmentIconSx, attachmentItemSx, attachmentLinkSx, attachmentAltProps, attachmentRowSx, attachmentUploadActionsSx, sectionSx } from './formStyles';

interface IFormAttachmentsSectionProps {
  ownerType: AttachmentOwnerType;
  ownerUuid: string;
  title: string;
  emptyText: string;
  editable?: boolean;
}

export default function FormAttachmentsSection({ ownerType, ownerUuid, title, emptyText, editable = false }: IFormAttachmentsSectionProps) {
  const [pendingFiles, setPendingFiles] = useState<JsonifiedFile[]>([]);
  const [keyToDestroy, setKeyToDestroy] = useState<string | null>(null);
  const { data: attachments, error: attachmentsError, isLoading } = useGetAttachmentsByOwner({ type: ownerType, uuid: ownerUuid, enabled: Boolean(ownerUuid) });
  const { mutate: uploadFiles, isPending: isUploading } = useCreateAttachments({
    type: ownerType,
    uuid: ownerUuid,
    onSuccessCallback: () => {
      setPendingFiles([]);
    },
  });
  const { mutate: destroyFiles, isPending: isDeleting } = useDestroyAttachments({
    type: ownerType,
    uuid: ownerUuid,
    onSuccessCallback: () => setKeyToDestroy(null),
  });

  const attachmentsList = attachments ?? [];
  const hasAttachments = !_.isEmpty(attachmentsList);

  const sortedAttachments = _.orderBy(attachmentsList, ['date'], ['desc']);

  if (isLoading) return editable ? <Loading /> : null;

  if (attachmentsError) {
    return (
      <Stack spacing={2.5} sx={sectionSx}>
        <Typography fontWeight="bold">{title}</Typography>
        <Typography {...attachmentAltProps}>Unable to load files right now.</Typography>
      </Stack>
    );
  }
  if (!editable && !hasAttachments) return null;
  const handleUpload = () => {
    if (_.isEmpty(pendingFiles)) return;
    uploadFiles(pendingFiles);
  };
  const handleDeleteConfirm = () => {
    if (!keyToDestroy) return;
    destroyFiles([keyToDestroy]);
  };

  const renderAttachment = (attachment: Attachment) => {
    const metadata = [humanizeTimestamp(attachment.date), formatSize(attachment.size)].filter(Boolean).join(' · ');

    return (
      <Box key={attachment.key} sx={attachmentRowSx}>
        <Box sx={attachmentItemSx}>
          <Box sx={attachmentIconSx}>{getFileTypeIcon(attachment.content_type)}</Box>
          <Box>
            <Link href={attachment.url} target="_blank" rel="noreferrer" underline="hover" color="inherit" sx={attachmentLinkSx}>
              {attachment.name}
            </Link>
            {metadata && <Typography {...attachmentAltProps}>{metadata}</Typography>}
          </Box>
        </Box>

        {editable && (
          <IconButton onClick={() => setKeyToDestroy(attachment.key)} disabled={isDeleting} aria-label={`Delete ${attachment.name}`}>
            <DeleteOutline />
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <Stack spacing={2.5} sx={sectionSx}>
      <Box>
        <Typography fontWeight="bold">{title}</Typography>
        {editable && <Typography {...attachmentAltProps}>Drag and drop files or click the area below to select them.</Typography>}
      </Box>

      {editable && (
        <Stack spacing={2}>
          <FileInput setFiles={setPendingFiles} files={pendingFiles} />
          <Box sx={attachmentUploadActionsSx}>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={_.isEmpty(pendingFiles) || isUploading}
              startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <FileUploadOutlined />}
            >
              Upload files
            </Button>
          </Box>
        </Stack>
      )}

      {hasAttachments ? (
        <Stack spacing={1.5}>{_.map(sortedAttachments, renderAttachment)}</Stack>
      ) : (
        <Typography {...attachmentAltProps}>{emptyText}</Typography>
      )}

      <ConfirmationModal text="Delete this file?" isOpen={!_.isNil(keyToDestroy)} onConfirm={handleDeleteConfirm} onCancel={() => setKeyToDestroy(null)} />
    </Stack>
  );
}
